import { useState } from "react";
import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQueryClient } from "@tanstack/react-query";
import { launchAgencyAudit } from "@/lib/agency.functions";

const GOLD = "#C8A84B";
const labelCls = "text-[10px] font-medium uppercase tracking-[0.18em] text-muted-foreground";
const inputCls =
  "mt-1.5 w-full rounded-xl border border-border bg-background px-4 py-3 text-sm text-foreground focus:border-foreground focus:outline-none";

export const Route = createFileRoute("/agency/new-audit")({
  component: NewAuditPage,
});

function NewAuditPage() {
  const launch = useServerFn(launchAgencyAudit);
  const router = useRouter();
  const qc = useQueryClient();
  const [state, setState] = useState({
    client_name: "",
    website_url: "",
    competitors: "",
    target_audience: "",
    notes: "",
  });
  const [status, setStatus] = useState<"idle" | "submitting" | "error">("idle");
  const [error, setError] = useState<string | null>(null);

  return (
    <div className="grid gap-6">
      <header>
        <p className="text-[10px] uppercase tracking-[0.22em]" style={{ color: GOLD }}>
          New audit
        </p>
        <h1 className="mt-2 font-display text-2xl font-semibold text-foreground">
          Launch a white-label audit
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          1 credit will be used. Report appears in your audits list when ready
          (~15–30 min). You QA and deliver to your client.
        </p>
      </header>

      <form
        onSubmit={async (e) => {
          e.preventDefault();
          setStatus("submitting");
          setError(null);
          try {
            const res = await launch({ data: state });
            if ("error" in res) throw new Error(res.error);
            await qc.invalidateQueries({ queryKey: ["agency-overview"] });
            router.navigate({ to: "/agency/audits" });
          } catch (err) {
            setStatus("error");
            setError(err instanceof Error ? err.message : "Could not launch audit");
          }
        }}
        className="grid gap-5 rounded-2xl border border-border bg-card/30 p-6"
      >
        <div className="grid gap-5 sm:grid-cols-2">
          <div>
            <label htmlFor="client_name" className={labelCls}>
              Client name *
            </label>
            <input
              id="client_name"
              required
              value={state.client_name}
              onChange={(e) => setState({ ...state, client_name: e.target.value })}
              className={inputCls}
              placeholder="Acme Co."
            />
          </div>
          <div>
            <label htmlFor="website_url" className={labelCls}>
              Client website *
            </label>
            <input
              id="website_url"
              type="url"
              required
              placeholder="https://acme.com"
              value={state.website_url}
              onChange={(e) => setState({ ...state, website_url: e.target.value })}
              className={inputCls}
            />
          </div>
        </div>
        <div>
          <label htmlFor="competitors" className={labelCls}>
            Top 3 competitors
          </label>
          <input
            id="competitors"
            placeholder="competitor1.com, competitor2.com, competitor3.com"
            value={state.competitors}
            onChange={(e) => setState({ ...state, competitors: e.target.value })}
            className={inputCls}
          />
        </div>
        <div>
          <label htmlFor="target_audience" className={labelCls}>
            Target audience
          </label>
          <input
            id="target_audience"
            value={state.target_audience}
            onChange={(e) =>
              setState({ ...state, target_audience: e.target.value })
            }
            className={inputCls}
          />
        </div>
        <div>
          <label htmlFor="notes" className={labelCls}>
            Internal notes
          </label>
          <textarea
            id="notes"
            rows={4}
            value={state.notes}
            onChange={(e) => setState({ ...state, notes: e.target.value })}
            className={inputCls}
          />
        </div>
        {error && (
          <p className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {error}
          </p>
        )}
        <div className="flex items-center justify-between gap-3 pt-2">
          <Link
            to="/agency"
            className="text-xs uppercase tracking-[0.18em] text-muted-foreground hover:text-foreground"
          >
            ← Cancel
          </Link>
          <button
            type="submit"
            disabled={status === "submitting"}
            className="rounded-full bg-foreground px-6 py-2.5 text-sm font-medium text-background hover:opacity-90 disabled:opacity-50"
          >
            {status === "submitting" ? "Launching…" : "Use 1 credit · Launch audit →"}
          </button>
        </div>
      </form>
    </div>
  );
}